import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import { Theme } from "@mui/material/styles";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import { Query, SearchPillData } from "../interfaces";

interface Props {
  dataArray: SearchPillData[];
  deleteSearchPill: (
    pill: SearchPillData,
    dataArray: SearchPillData[],
    appState?: Query,
  ) => void;
  standardColor?: boolean;
  secondaryColor?: boolean;
  errorColor?: boolean;
  appState?: Query;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
      justifyContent: "flex-start",
      flexWrap: "wrap",
      listStyle: "none",
      padding: 0,
      margin: 0,
      boxShadow: "none",
    },
    pillStyle: {
      "backgroundColor": theme.palette.secondary.main,
      "border": "1px solid #acc8df",
      "marginTop": ".25rem",
      "marginBottom": ".25rem",
      "whiteSpace": "break-spaces",
      "marginLeft": ".5rem",
      "fontWeight": 400,
      "fontStyle": "normal",
      "fontSize": 12,
      "color": theme.palette.primary.main,
      "&:focus": {
        "& .MuiSvgIcon-root": {
          color: "white",
          backgroundColor: theme.palette.primary.main,
          border: "solid 1",
          borderRadius: ".625rem",
        },
      },
      "&.MuiChip-root": {
        height: 20,
        width: "flex",
      },
      "& .MuiChip-label": {
        paddingLeft: 8,
        whiteSpace: "break-spaces",
      },
      "& .MuiSvgIcon-root": {
        "marginRight": 0,
        "color": theme.palette.primary.main,
        "&:hover": {
          color: "white",
          backgroundColor: theme.palette.primary.main,
          border: "solid 1",
          borderRadius: ".625rem",
        },
      },
    },
    errorPillColor: {
      "borderColor": theme.palette.error.dark,
      "backgroundColor": "#f9e5e5",
      "color": theme.palette.error.dark,
      "&:hover": {
        backgroundColor: "#f9e5e5",
      },
      "&:focus": {
        "backgroundColor": "#f9e5e5",
        "& .MuiSvgIcon-root": {
          color: "white",
          backgroundColor: theme.palette.error.dark,
          border: "solid 1",
          borderRadius: ".625rem",
        },
      },
      "& .MuiSvgIcon-root": {
        "color": theme.palette.error.dark,
        "&:hover": {
          color: "white",
          backgroundColor: theme.palette.error.dark,
          border: "solid 1",
          borderRadius: ".625rem",
        },
      },
    },
    secondaryPillColor: {
      borderColor: "#00908C",
      backgroundColor: "#E6FFFF",
    },
    standardColor: {
      borderColor: "#70643e",
      backgroundColor: "#ede7d3",
    },
    divider: {
      margin: "-1rem 0 1rem 0",
    },
  }),
);
export default function SearchPills({
  dataArray,
  deleteSearchPill,
  standardColor,
  secondaryColor,
  errorColor,
  appState,
}: Props) {
  const classes = useStyles();
  return (
    <Paper
      component="ul"
      className={clsx(classes.root, "searchPillsContainer")}
    >
      {(dataArray ?? []).map((data: SearchPillData, index: number) => {
        const dataVal = data.value
          ? Array.isArray(data.value)
            ? data.value
                .join(" ")
                .toLowerCase()
                .replace(/ /g, "-")
            : data.value.toLowerCase().replace(/ /g, "-")
          : "";
        const duplicateLabel =
          dataArray
            .map((val: any) => val.label)
            .filter(val => val === data.label).length > 1;

        const searchPillId = data.label
          ? `${data.label
              .toLowerCase()
              .replace(/[^A-Z0-9]/gi, "-")
              .replace(/[-]+/g, "-")}-search-pills${
              duplicateLabel && dataVal ? `-${dataVal}` : ""
            }`
          : `${dataVal}-search-pills`;
        return data.value ? (
          <li key={index}>
            <Chip
              label={
                data.label
                  ? `${data.label}: ${
                      Array.isArray(data.value)
                        ? data.value.join(", ")
                        : data.value
                    }`
                  : data.value
              }
              onDelete={
                data.static
                  ? undefined
                  : () => deleteSearchPill(data, dataArray, appState)
              }
              sx={{
                "& .MuiChip-deleteIcon": {
                  fontSize: "1.25rem",
                },
              }}
              className={clsx(
                classes.pillStyle,
                standardColor && classes.standardColor,
                secondaryColor && classes.secondaryPillColor,
                errorColor && classes.errorPillColor,
              )}
              id={searchPillId}
            />
          </li>
        ) : (
          ""
        );
      })}
    </Paper>
  );
}
